<template>
  <div class="legal-page">
    <!-- Intro Section -->
    <PolicyIntroComp :content="introContent" />

    <!-- Privacy Content Section -->
    <PolicyContentComp :content="contentData" />

  </div>
</template>

<script>
import { onMounted, ref } from 'vue'
import WOW from 'wowjs'
import PolicyIntroComp from '@/components/PolicyIntroComp.vue'
import PolicyContentComp from '@/components/PolicyContentComp.vue'

export default {
  name: 'PrivacyPage',
  components: {
    PolicyIntroComp,
    PolicyContentComp
  },
  setup() {
    onMounted(() => {
      // Initialize WOW.js
      new WOW.WOW({
        mobile: false
      }).init()
    })

    // Intro content object
    const introContent = ref({
      sectionLabel: "Privacy & Data",
      mainTitle: "Privacy Policy",
      description: "Your privacy is important to us. This policy explains how we collect, use, and protect your personal information in compliance with GDPR and other privacy regulations."
    })

    // Privacy content object
    const contentData = ref({
      sections: [
        {
          title: "1. Information We Collect",
          paragraphs: [
            {
              type: "text",
              content: "We collect information you provide directly to us, such as when you create an account, use our services, or contact us for support. This may include:"
            },
            {
              type: "unordered",
              items: [
                "Personal identification information (name, email address, phone number)",
                "Business information (company name, job title, industry)",
                "Project requirements and specifications",
                "Communication records and support interactions",
                "Payment and billing information"
              ]
            }
          ]
        },
        {
          title: "2. How We Use Your Information",
          paragraphs: [
            {
              type: "text",
              content: "We use the information we collect to:"
            },
            {
              type: "unordered",
              items: [
                "Provide, maintain, and improve our services",
                "Process transactions and send related information",
                "Send technical notices, updates, and support messages",
                "Respond to your comments and questions",
                "Comply with legal obligations and enforce our agreements"
              ]
            }
          ]
        },
        {
          title: "3. Information Sharing and Disclosure",
          paragraphs: [
            {
              type: "text",
              content: "We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except in the following circumstances:"
            },
            {
              type: "unordered",
              items: [
                "With your explicit consent",
                "To comply with legal obligations",
                "To protect our rights and prevent fraud",
                "With trusted service providers who assist in our operations"
              ]
            }
          ]
        },
        {
          title: "4. Data Security",
          paragraphs: [
            {
              type: "text",
              content: "We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. Our security measures include:"
            },
            {
              type: "unordered",
              items: [
                "Encryption of data in transit and at rest",
                "Regular security assessments and updates",
                "Access controls and authentication systems",
                "Employee training on data protection practices"
              ]
            }
          ]
        },
        {
          title: "5. Cookies and Tracking Technologies",
          paragraphs: [
            {
              type: "text",
              content: "We use cookies and similar tracking technologies to enhance your experience on our website. These technologies help us:"
            },
            {
              type: "unordered",
              items: [
                "Remember your preferences and settings",
                "Analyze website traffic and usage patterns",
                "Improve website functionality and performance",
                "Provide personalized content and advertisements"
              ]
            },
            {
              type: "text",
              content: "You can control cookie settings through your browser preferences."
            }
          ]
        },
        {
          title: "6. GDPR Compliance",
          paragraphs: [
            {
              type: "text",
              content: "For users in the European Union, we comply with the General Data Protection Regulation (GDPR). Your rights include:"
            },
            {
              type: "unordered",
              items: [
                "<strong>Right to Access:</strong> Request copies of your personal data",
                "<strong>Right to Rectification:</strong> Correct inaccurate or incomplete data",
                "<strong>Right to Erasure:</strong> Request deletion of your personal data",
                "<strong>Right to Portability:</strong> Receive your data in a structured format",
                "<strong>Right to Object:</strong> Opt-out of certain data processing activities"
              ]
            }
          ]
        },
        {
          title: "7. Data Retention",
          paragraphs: [
            {
              type: "text",
              content: "We retain your personal information for as long as necessary to fulfill the purposes outlined in this privacy policy, unless a longer retention period is required or permitted by law. We will delete or anonymize your data when it is no longer needed."
            }
          ]
        },
        {
          title: "8. International Data Transfers",
          paragraphs: [
            {
              type: "text",
              content: "Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your data during international transfers, including standard contractual clauses and adequacy decisions."
            }
          ]
        },
        {
          title: "9. Children's Privacy",
          paragraphs: [
            {
              type: "text",
              content: "Our services are not intended for children under 16 years of age. We do not knowingly collect personal information from children under 16. If we become aware that we have collected personal information from a child under 16, we will take steps to delete such information."
            }
          ]
        },
        {
          title: "10. Changes to This Policy",
          paragraphs: [
            {
              type: "text",
              content: "We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the \"Last Updated\" date. We encourage you to review this policy periodically."
            }
          ]
        },
        {
          title: "11. Contact Us",
          paragraphs: [
            {
              type: "text",
              content: "If you have any questions about this privacy policy or our data practices, please contact us at:"
            }
          ],
          contactInfo: [
            { label: "Data Protection Officer", value: "privacy@sogdo.com" },
            { label: "General Inquiries", value: "hello@sogdo.com" },
            { label: "Address", value: "123 Tech Street, Innovation District, San Francisco, CA 94105" }
          ]
        }
      ]
    })

    return {
      introContent,
      contentData
    }
  }
}
</script>
